import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import Paper from '@mui/material/Paper';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Grid from '@mui/material/Unstable_Grid2';
import { styled } from '@mui/material/styles';
import ProgressBar from '@ramonak/react-progress-bar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { DEFAULT_THEME_COLOR } from '../constants/theme';
import useTranslation from '../customHooks/translations';
import Typography from '@mui/material/Typography';
import agent from '../agent';
import exifr from 'exifr';

const Item = styled('div')(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme?.palette?.background?.level1 : '#fff',
  padding: theme.spacing(1),
  textAlign: 'center',
  borderRadius: 4,
  color: theme?.vars?.palette?.text?.secondary,
}));

const fileSizeLimitApi = 100000000; // 100mb

const getExifLocation = async (file) => {
  try {
    const gps = await exifr.gps(file);
    if (gps?.latitude != null && gps?.longitude != null) {
      return { latitude: gps.latitude, longitude: gps.longitude };
    }
  } catch {}
  return null;
};

const getCurrentLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000 },
    );
  });

const saveFiles = async (
  acceptedFiles,
  device,
  props,
  setProgress,
  setSaveDisabled,
  setUploadFileError,
  translation,
  username,
  latitude,
  longitude,
) => {
  setUploadFileError(null);
  if (setSaveDisabled) setSaveDisabled(true);

  for (let i = 0; i < acceptedFiles.length; i++) {
    const fileType = acceptedFiles[i].type;
    const fileSize = acceptedFiles[i].size;

    if (fileSize && fileSize > fileSizeLimitApi) {
      setUploadFileError(translation.uploadFiles.tooLargeFile);
      if (setSaveDisabled) setSaveDisabled(false);
      return;
    }
    if (!fileType.includes('image')) {
      setUploadFileError(translation.uploadFiles.notSupportedFileType);
      if (setSaveDisabled) setSaveDisabled(false);
      return;
    }

    const extension = fileType.split('/')[1];

    try {
      const { presignedUrl, s3Path } = await agent.Upload.getPreSignedUrl(props.dest, fileSize);

      await axios.put(presignedUrl, acceptedFiles[i], {
        onUploadProgress: (progress) => {
          const percentCompleted = Math.round((progress.loaded * 100) / progress.total);
          setProgress(Math.round(percentCompleted / (acceptedFiles.length - i)));
        },
      });

      const results = await agent.Upload.createImagesForDevices({ s3Path, extension, device });

      props.onFileUpload({
        data: { ...results },
        type: 'images',
        ...(latitude != null && longitude != null ? { latitude, longitude } : {}),
      });
    } catch (error) {
      const errorJson = (() => { try { return JSON.parse(error?.message); } catch { return {}; } })();
      const errorMessage = errorJson?.errors?.message || translation.uploadFiles.somethingWentWrong;
      setUploadFileError(errorMessage);
    }
  }

  setProgress(0);
  if (setSaveDisabled) setSaveDisabled(false);
};

const UploadFiles = (props) => {
  const translation = useTranslation();
  const device = props.device;
  const [progress, setProgress] = useState(0);
  const [uploadFileError, setUploadFileError] = useState(null);
  const [pendingFiles, setPendingFiles] = useState(null);
  const [pendingExifLocation, setPendingExifLocation] = useState(null);
  const [pendingCurrentLocation, setPendingCurrentLocation] = useState(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  const runUpload = useCallback(
    (files, latitude, longitude) => {
      saveFiles(
        files,
        device,
        props,
        setProgress,
        props.setSaveDisabled,
        setUploadFileError,
        translation,
        props?.currentUser?.username,
        latitude,
        longitude,
      );
    },
    [device, props.dest, props.setSaveDisabled, props.onFileUpload, props.currentUser, translation],
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      const [exifLocation, currentLocation] = await Promise.all([
        getExifLocation(acceptedFiles[0]),
        getCurrentLocation(),
      ]);

      if (exifLocation || currentLocation) {
        setPendingFiles(acceptedFiles);
        setPendingExifLocation(exifLocation);
        setPendingCurrentLocation(currentLocation);
        setLocationDialogOpen(true);
      } else {
        runUpload(acceptedFiles, undefined, undefined);
      }
    },
    [runUpload],
  );

  const handleLocationChoice = (location) => {
    setLocationDialogOpen(false);
    const files = pendingFiles;
    setPendingFiles(null);
    setPendingExifLocation(null);
    setPendingCurrentLocation(null);
    runUpload(files, location?.latitude, location?.longitude);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <>
      <Paper {...getRootProps()} sx={{ boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)' }}>
        <input {...getInputProps()} />
        <Grid container>
          <Grid xs={12}>
            <Item>
              {uploadFileError ? (
                <Typography variant="h6" sx={{ color: 'red' }}>
                  {uploadFileError}
                </Typography>
              ) : null}
            </Item>
            <Item>
              <CloudUploadIcon sx={{ width: 40, height: 40 }} />
            </Item>
            {progress ? <ProgressBar completed={progress} bgColor={DEFAULT_THEME_COLOR} /> : null}
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={locationDialogOpen} onClose={() => handleLocationChoice(null)}>
        <DialogTitle>{translation.uploadFiles.locationChoice.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translation.uploadFiles.locationChoice.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
          {pendingExifLocation && (
            <Button
              variant="contained"
              sx={{ bgcolor: DEFAULT_THEME_COLOR }}
              onClick={() => handleLocationChoice(pendingExifLocation)}
            >
              {translation.uploadFiles.locationChoice.useExif}
            </Button>
          )}
          {pendingCurrentLocation && (
            <Button
              variant="outlined"
              onClick={() => handleLocationChoice(pendingCurrentLocation)}
            >
              {translation.uploadFiles.locationChoice.useCurrent}
            </Button>
          )}
          <Button variant="text" onClick={() => handleLocationChoice(null)}>
            {translation.uploadFiles.locationChoice.withoutLocation}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  currentUser: state.common.currentUser,
  device: state.common.device,
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(UploadFiles);
